import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import getConfig from '../../../app/config';
import fetchWithRefresh from '../../../services/fetchWithRefresh';
import { RootState } from '../../../app/store';
import { toast } from 'react-toastify';
import { ApiToken, ApiTokenSummary, CreateApiTokenRequest, CreateApiTokenResponse, RenewApiTokenRequest, RenewApiTokenResponse, UpdateApiTokenRequest, UpdateApiTokenResponse } from './types';

export interface ApiTokensState {
  loading: boolean;
  list: ApiTokenSummary[];
  token: ApiToken | null;
  saving: boolean;
  deletingIds: string[];
  error: any;
  newlyCreatedToken?: string | null; // full token value to show once
}

const initialState: ApiTokensState = {
  loading: false,
  list: [],
  saving: false,
  deletingIds: [],
  error: null,
  newlyCreatedToken: null,
  token: null,
};

export const fetchApiTokens = createAsyncThunk<ApiTokenSummary[], void>(
  'apiTokens/fetchAll',
  async (_, thunkApi) => {
    const response = await fetchWithRefresh(getConfig().apiUrl + 'apiToken', thunkApi, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    return response?.apiTokens || [];
  }
);

export const fetchApiToken = createAsyncThunk<ApiToken | null, string>(
  'apiTokens/fetch',  
  async (id, thunkApi) => {    
    const response = await fetchWithRefresh(getConfig().apiUrl + 'apiToken/' + id, thunkApi, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    return response as ApiToken || null;
  }
);

export const createApiToken = createAsyncThunk<CreateApiTokenResponse, CreateApiTokenRequest>(
  'apiTokens/create',
  async (req, thunkApi) => {
    const response = await fetchWithRefresh(getConfig().apiUrl + 'apiToken', thunkApi, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(req)
    });
    return response as CreateApiTokenResponse;
  }
);

export const updateApiToken = createAsyncThunk<UpdateApiTokenResponse, UpdateApiTokenRequest>(
  'apiTokens/update',
  async (request, thunkApi) => {
    const response = await fetchWithRefresh(getConfig().apiUrl + 'apiToken', thunkApi, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(request)
    });
    return response;
  }
);

export const renewApiToken = createAsyncThunk<RenewApiTokenResponse, RenewApiTokenRequest>(
  'apiTokens/renew',
  async (request, thunkApi) => {
    const response = await fetchWithRefresh(getConfig().apiUrl + `apiToken/renew/${request.originalApiTokenId}`, thunkApi, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(request)
    });
    return response as RenewApiTokenResponse;
  }
);

export const deleteApiToken = createAsyncThunk<{ success: boolean; id: string }, { id: string }>(
  'apiTokens/delete',
  async ({ id }, thunkApi) => {
    const response = await fetchWithRefresh(getConfig().apiUrl + 'apiToken/' + id, thunkApi, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        body:  JSON.stringify({})
      },
    });
    return { success: response?.success || response?.status === 200, id };
  }
);

const apiTokensSlice = createSlice({
  name: 'apiTokens',
  initialState,
  reducers: {
    clearNewlyCreatedToken: (state) => { state.newlyCreatedToken = null; },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchApiTokens.pending, (state) => { 
      state.loading = true; 
      state.error = null; 
      state.newlyCreatedToken = null;
    });
    builder.addCase(fetchApiTokens.fulfilled, (state, { payload }) => { 
      state.loading = false; 
      state.list = payload; 
    });
    builder.addCase(fetchApiTokens.rejected, (state, action) => { 
      state.loading = false; 
      state.error = action.error; 
    });

    builder.addCase(fetchApiToken.pending, (state) => { 
      state.loading = true; 
      state.error = null; 
      state.newlyCreatedToken = null;
    });
    builder.addCase(fetchApiToken.fulfilled, (state, { payload }) => {       
      state.loading = false; 
      state.token = payload;
    });
    builder.addCase(fetchApiToken.rejected, (state, action) => { 
      state.loading = false; 
      state.error = action.error; 
    });

    builder.addCase(createApiToken.pending, (state) => { 
      state.saving = true; 
      state.error = null; 
      state.newlyCreatedToken = null; 
    });
    builder.addCase(createApiToken.fulfilled, (state, { payload }) => {
      state.saving = false;
      if (payload?.success) {        
        if (payload.apiToken) state.list.push(payload.apiToken);
        if (payload.token) state.newlyCreatedToken = payload.token;
        toast.success('API token created');
      } else {        
        state.error = payload?.errors;
        toast.error('Error creating API token');
      }
    });
    builder.addCase(createApiToken.rejected, (state, action) => {            
      state.saving = false;             
      state.error = { errors: action.payload }; 
      toast.error('Error creating API token');
    });
   
    builder.addCase(updateApiToken.pending, (state) => { 
      state.saving = true; 
      state.error = null; 
      state.newlyCreatedToken = null; 
    });
    builder.addCase(updateApiToken.fulfilled, (state, { payload }) => {
      state.saving = false;
      if (payload?.success) {        
        if (payload.apiToken) state.list.push(payload.apiToken);
        if (payload.token) state.newlyCreatedToken = payload.token;
        toast.success('API token updated');
      } else {
        state.error = payload?.errors;
        toast.error('Error updating API token');
      }
    });
    builder.addCase(updateApiToken.rejected, (state, action) => {      
      state.saving = false;             
      state.error = { errors: action.payload }; 
      toast.error('Error updating API token');
    });

    builder.addCase(renewApiToken.pending, (state) => { 
      state.saving = true; 
      state.error = null; 
      state.newlyCreatedToken = null; 
    });
    builder.addCase(renewApiToken.fulfilled, (state, { payload }) => {
      state.saving = false;
      if (payload?.success) {        
        if (payload.apiToken) {
           state.list.push(payload.apiToken);
        }

        if (payload.token) {
           state.newlyCreatedToken = payload.token;
        }

        toast.success('API token renewed');
      } else {
        state.error = payload?.errors;
        toast.error('Error renewing API token');
      }
    });
    builder.addCase(renewApiToken.rejected, (state, action) => {          
      state.saving = false;
      state.error = { errors: action.payload }; 
      toast.error('Error renewing API token');
    });

    builder.addCase(deleteApiToken.pending, (state, action) => { state.deletingIds.push(action.meta.arg.id); });
    builder.addCase(deleteApiToken.fulfilled, (state, { payload }) => {
      state.deletingIds = state.deletingIds.filter(i => i !== payload.id);
      if (payload.success) {
        state.list = state.list.filter(t => t.id !== payload.id);
        toast.success('API token deleted');
      } else {
        toast.error('Error deleting API token');
      }
    });
    builder.addCase(deleteApiToken.rejected, (state, action) => {
      const id = action.meta.arg.id;
      state.deletingIds = state.deletingIds.filter(i => i !== id);
      toast.error('Error deleting API token');
    });
  }
});

export const { clearNewlyCreatedToken } = apiTokensSlice.actions;

export const selectApiTokens = (state: RootState) => state.apiTokens;

export default apiTokensSlice.reducer;
